import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PrimaryGrid } from '@ui/components/common/Grid';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { StyledMenu } from '@ui/components/common/Menu';
import ReassignVehicleDialog from '../LocationContent/LocationDetail/ReassignVehicleDialog';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getAssignedAttendeesByEventIdAsync, unassignAttendeeFromVehicleAsync } from '@application/features/attendeePickupAssignment/attendeePickupAssignmentThunk';
import { formatRegisteredDateOnly } from '@application/utilities/date-helper';
import { toast } from 'react-toastify';
import { getPickupLocationsNotPaginatedByEventIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { FamilyFestGetPickupLocationsNotPaginatedAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { getAssignedFamilyFestAttendeesByEventIdAsync, unassignFamilyFestAttendeeFromVehicleAsync } from '@application/features/familyFest/familyFestPickupAssignmentThunk';
const AssignedAttendeesContent = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { eventId } = useParams();
    const dispatch = useAppDispatch();
    const [searchValue, setSearchValue] = useState('');
    const [gridFilterOpen, setGridFilterOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [reassignVehicleOpen, setReassignVehicleOpen] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [selectedAttendee, setSelectedAttendee] = useState(null);
    const [filters, setFilters] = useState({});
    const [resetFiltersKey, setResetFiltersKey] = useState(0);
    const { assignedAttendees: standardAssignedAttendees } = useAppSelector((state) => state.attendeePickupAssignment);
    const { assignedAttendees: familyFestAssignedAttendees } = useAppSelector((state) => state.familyFestPickupAssignment);
    const { pickupLocationsList } = useAppSelector((state) => state.pickupLocation);
    const { VDKEventsForEventDetails } = useAppSelector((state) => state.VDKEvent);
    // Assigned attendees and the pickup-location filter options are both scoped by the selected
    // companies via the auth token, so both must be refetched on a sidebar company change.
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const isFamilyFest = VDKEventsForEventDetails?.type === EventTypeEnum.Family;
    const assignedAttendees = isFamilyFest ? familyFestAssignedAttendees : standardAssignedAttendees;
    // Fetch pickup locations for filter dropdown
    useEffect(() => {
        if (eventId) {
            if (isFamilyFest) {
                dispatch(FamilyFestGetPickupLocationsNotPaginatedAsync({
                    includeClosed: false,
                    searchString: undefined
                }));
            }
            else {
                dispatch(getPickupLocationsNotPaginatedByEventIdAsync({
                    eventId: eventId,
                    includeClosed: false,
                    searchString: undefined
                }));
            }
        }
    }, [eventId, dispatch, isFamilyFest, selectedCompanyIds]);
    // Map pickup locations to filter options
    const locationFilterOptions = useMemo(() => {
        if (!pickupLocationsList)
            return [];
        return pickupLocationsList.map((location) => ({
            value: location.id,
            label: location.name || ''
        }));
    }, [pickupLocationsList]);
    useEffect(() => {
        if (eventId) {
            if (isFamilyFest) {
                const sort = sortModel[0];
                const sortFieldMap = {
                    location: 'pickuplocationname',
                    company: 'companyname',
                    type: 'attendeetype',
                    registered: 'createdon'
                };
                dispatch(getAssignedFamilyFestAttendeesByEventIdAsync({
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    name: filters.name,
                    vehicleNumber: filters.vehicleNumber,
                    pickupLocationId: filters.location,
                    companyName: filters.company,
                    attendeeType: filters.type,
                    primaryAttendeeName: filters.primaryPerson,
                    registeredDate: filters.registered,
                    sortBy: sort ? (sortFieldMap[sort.field] ?? sort.field) : undefined,
                    sortOrder: sort?.sort ?? 'desc'
                }));
            }
            else {
                dispatch(getAssignedAttendeesByEventIdAsync({
                    eventId: eventId,
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    searchString: searchValue || undefined,
                    name: filters.name,
                    vehicleNumber: filters.vehicleNumber,
                    pickupLocationId: filters.location,
                    companyName: filters.company,
                    attendeeType: filters.type,
                    registeredDate: filters.registered
                }));
            }
        }
    }, [dispatch, eventId, paginationModel.page, paginationModel.pageSize, searchValue, filters, sortModel, isFamilyFest, selectedCompanyIds]);
    const handleMenuClose = useCallback(() => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    }, []);
    const handleReassignVehicleClick = useCallback(() => {
        const currentMenuRow = menuRow;
        handleMenuClose();
        if (currentMenuRow) {
            const items = assignedAttendees?.items;
            const attendee = items?.find((item) => item.id === currentMenuRow.id);
            if (attendee) {
                setSelectedAttendee(attendee);
            }
            else {
                setSelectedAttendee(null);
            }
        }
        setReassignVehicleOpen(true);
    }, [handleMenuClose, menuRow]);
    const handleSendToUnassignedClick = useCallback(() => {
        const currentMenuRow = menuRow;
        handleMenuClose();
        if (currentMenuRow) {
            const attendee = assignedAttendees?.items?.find((item) => item.id === currentMenuRow.id);
            if (attendee) {
                if (isFamilyFest) {
                    dispatch(unassignFamilyFestAttendeeFromVehicleAsync([attendee.id]))
                        .unwrap()
                        .then((response) => {
                        if (response.isSuccess) {
                            toast.success(response.message ||
                                t('events.eventDetails.transportTab.attendeeUnassignedSuccessfully'));
                            // Refresh the list
                            setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                            if (eventId) {
                                dispatch(getAssignedFamilyFestAttendeesByEventIdAsync({
                                    pageNumber: 1,
                                    pageSize: paginationModel.pageSize,
                                    searchString: searchValue || undefined,
                                    name: filters.name,
                                    vehicleNumber: filters.vehicleNumber,
                                    pickupLocationId: filters.location,
                                    companyName: filters.company,
                                    attendeeType: filters.type,
                                    primaryAttendeeName: filters.primaryPerson,
                                    registeredDate: filters.registered
                                }));
                            }
                        }
                        else {
                            toast.error(response.message ||
                                t('events.eventDetails.transportTab.attendeeUnassignFailed'));
                        }
                    })
                        .catch((error) => {
                        const errorMessage = error?.message ||
                            t('events.eventDetails.transportTab.attendeeUnassignFailed');
                        toast.error(errorMessage);
                    });
                }
                else {
                    dispatch(unassignAttendeeFromVehicleAsync([attendee.id]))
                        .unwrap()
                        .then((response) => {
                        if (response.isSuccess) {
                            toast.success(response.message ||
                                t('events.eventDetails.transportTab.attendeeUnassignedSuccessfully'));
                            // Refresh the list
                            setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                            if (eventId) {
                                dispatch(getAssignedAttendeesByEventIdAsync({
                                    eventId: eventId,
                                    pageNumber: 1,
                                    pageSize: paginationModel.pageSize,
                                    searchString: searchValue || undefined,
                                    name: filters.name,
                                    vehicleNumber: filters.vehicleNumber,
                                    pickupLocationId: filters.location,
                                    companyName: filters.company,
                                    attendeeType: filters.type,
                                    registeredDate: filters.registered
                                }));
                            }
                        }
                        else {
                            toast.error(response.message ||
                                t('events.eventDetails.transportTab.attendeeUnassignFailed'));
                        }
                    })
                        .catch((error) => {
                        const errorMessage = error?.message ||
                            t('events.eventDetails.transportTab.attendeeUnassignFailed');
                        toast.error(errorMessage);
                    });
                }
            }
        }
    }, [handleMenuClose, menuRow, dispatch, isFamilyFest, eventId, paginationModel.pageSize, searchValue, filters, t, assignedAttendees?.items]);
    const rowMenuActions = useMemo(() => [
        {
            text: t('events.eventDetails.transportTab.sendToUnassigned'),
            group: true,
            to: handleSendToUnassignedClick
        },
        {
            text: t('events.eventDetails.transportTab.reassignVehicle'),
            to: handleReassignVehicleClick,
            group: true
        }
    ], [t, handleReassignVehicleClick, handleSendToUnassignedClick]);
    const columns = useMemo(() => [
        {
            field: 'name',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.name'),
            flex: 1.2,
            minWidth: 160,
            renderCell: (params) => (_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 40, children: [_jsx(Typography, { variant: 'body2', children: params.row.name }), params.row.hasBirthdayOnEventDate && (_jsx(Typography, { variant: 'caption', color: 'text.secondary', children: t('events.eventDetails.transportTab.birthday') }))] }))
        },
        {
            field: 'vehicleNumber',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.vehicleNumber'),
            flex: 0.9,
            minWidth: 120
        },
        {
            field: 'location',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.location'),
            flex: 1.4,
            minWidth: 220
        },
        {
            field: 'company',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.company'),
            flex: 1,
            minWidth: 140
        },
        {
            field: 'type',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.type'),
            flex: 0.8,
            minWidth: 110
        },
        ...(isFamilyFest
            ? [
                {
                    field: 'primaryPerson',
                    type: 'string',
                    headerName: t('events.eventDetails.transportTab.primaryPerson'),
                    flex: 1,
                    minWidth: 160,
                    sortable: false,
                    filterable: false
                }
            ]
            : []),
        {
            field: 'registered',
            type: 'string',
            headerName: t('events.eventDetails.transportTab.registered'),
            flex: 1,
            minWidth: 140
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.4,
            minWidth: 80,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (_jsx(Box, { display: 'flex', justifyContent: 'flex-end', width: '100%', children: _jsx(IconButton, { size: 'small', onClick: (e) => {
                        e.stopPropagation();
                        setMenuAnchorEl(e.currentTarget);
                        setMenuRow(params.row);
                    }, children: _jsx(MoreHorizIcon, {}) }) }))
        }
    ], [t, isFamilyFest]);
    const rows = useMemo(() => {
        if (!assignedAttendees?.items)
            return [];
        return assignedAttendees.items.map((item) => ({
            id: String(item.id),
            name: item.name || '—',
            routeName: item.routeName || '—',
            vehicleNumber: item.vehicleNumber || '—',
            location: item.pickupLocationName || '—',
            company: item.companyName || item.company || '—',
            type: item.attendeeType || '—',
            primaryPerson: item.primaryAttendeeName || '-',
            registered: formatRegisteredDateOnly(item.createdDate),
            hasBirthdayOnEventDate: item.hasBirthdayOnEventDate
        }));
    }, [assignedAttendees?.items]);
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { columns: columns, rows: rows, filterOpen: gridFilterOpen, onFilterOpenChange: setGridFilterOpen, onApplyFilters: (newFilters) => {
                    setFilters(newFilters);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, filterOptions: {
                    location: locationFilterOptions
                }, resetFiltersKey: resetFiltersKey, showHeaderControls: false, showNewHeaderControls: true, paginationMode: 'server', paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, rowCount: assignedAttendees?.totalCount || 0, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, pageSizeOptions: [5, 10, 25, 50, 100], maxHeight: 'calc(100dvh - 250px)', customButton: _jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: isSmallScreen ? undefined : 'center', flexDirection: isSmallScreen ? 'column' : 'row', gap: isSmallScreen ? '12px' : 0, children: [_jsx(Typography, { variant: 'h4', children: t('events.eventDetails.transportTab.assignedAttendees') }), _jsx(Box, { display: 'flex', alignItems: 'center', gap: 0.5, children: _jsx(SearchFilter, { forceDesktopVariant: true, fullWidth: true, searchValue: searchValue, onSearchChange: (value) => setSearchValue(value), placeholder: t('events.eventDetails.transportTab.searchAttendees'), onClear: () => {
                                    setSearchValue('');
                                }, showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                    setFilters({});
                                    setResetFiltersKey((k) => k + 1);
                                }, widthBox: '220px', isDatagrid: true, iconButtonSize: 'medium', heightBox: '42px', onFilterClick: () => setGridFilterOpen(true), filterLabel: t('users.filters') }) })] }), boxProps: { sx: { display: 'flex', flexDirection: 'column', gap: '12px' } } }), _jsx(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleMenuClose, actions: rowMenuActions, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } }), _jsx(ReassignVehicleDialog, { open: reassignVehicleOpen, onClose: () => setReassignVehicleOpen(false), title: t('events.eventDetails.transportTab.reassignVehicle'), locationId: selectedAttendee?.pickupLocationId, isFamilyFest: isFamilyFest, onSuccess: () => {
                    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
                    if (eventId) {
                        if (isFamilyFest) {
                            dispatch(getAssignedFamilyFestAttendeesByEventIdAsync({
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined,
                                vehicleNumber: filters.vehicleNumber,
                                pickupLocationId: filters.location,
                                companyName: filters.company,
                                attendeeType: filters.type,
                                primaryAttendeeName: filters.primaryPerson
                            }));
                        }
                        else {
                            dispatch(getAssignedAttendeesByEventIdAsync({
                                eventId: eventId,
                                pageNumber: 1,
                                pageSize: paginationModel.pageSize,
                                searchString: searchValue || undefined,
                                vehicleNumber: filters.vehicleNumber,
                                pickupLocationId: filters.location,
                                companyName: filters.company,
                                attendeeType: filters.type
                            }));
                        }
                    }
                } })] }));
};
export default AssignedAttendeesContent;
